<template>
  <div class="school-event-form w-full mt-4">
    <div class="md-layout mb-4">
      <div class="md-layout-item md-size-50">
        <validation-field name="event name" :validations="{ required: true }">
          <label>{{ $t("common.name") }}</label>
          <md-input v-model="eventCfg.name" required></md-input>
        </validation-field>
      </div>
      <div class="md-layout-item md-size-30">
        <md-field>
          <label>{{ $t("schedule.category") }}</label>
          <md-select
            v-model="eventCfg.category"
            name="categories"
            id="categories"
            required
          >
            <md-option
              v-for="(title, key) in categories"
              :value="key"
              :key="'opt' + key"
            >
              {{ title }}
            </md-option>
          </md-select>
        </md-field>
      </div>
      <div class="md-layout-item md-size-20">
        <md-switch v-model="eventCfg.isAllDay" class="isis-switch-field">
          {{ $t("schedule.all_day") }}
        </md-switch>
      </div>
    </div>
    <div class="md-layout mb-4">
      <div class="md-layout-item md-size-25">
        <date-picker-custom
          v-model="eventCfg.startDate"
          :label="$t('common.start_date')"
        ></date-picker-custom>
      </div>
      <div class="md-layout-item md-size-20" v-if="!eventCfg.isAllDay">
        <isis-time-picker v-model="eventCfg.startTime" />
      </div>
      <span class="md-layout-item md-size-10 flex items-center justify-center">
        {{ $t("common.to") }}
      </span>
      <div class="md-layout-item md-size-25">
        <date-picker-custom
          v-model="eventCfg.endDate"
          :label="$t('common.end_date')"
        ></date-picker-custom>
      </div>
      <div class="md-layout-item md-size-20" v-if="!eventCfg.isAllDay">
        <isis-time-picker v-model="eventCfg.endTime" />
      </div>
    </div>
    <div class="md-layout mb-4">
      <div class="md-layout-item md-size-30">
        <md-field>
          <label>{{ $t("schedule.location") }}</label>
          <md-input v-model="eventCfg.location"></md-input>
        </md-field>
      </div>
      <div class="md-layout-item md-size-30" v-if="!eventCfg.isAllDay">
        <isis-autocomplete
          v-model="tzDisplayValue"
          :value.sync="eventCfg.timezone"
          :options="timeZoneOpts"
          :label="$t('timezone.timezone')"
        >
        </isis-autocomplete>
      </div>
      <div class="md-layout-item md-size-40">
        <md-field>
          <label>{{ $t("common.description") }}</label>
          <md-input v-model="eventCfg.description"></md-input>
        </md-field>
      </div>
    </div>
  </div>
</template>

<script>
import { EVENT_CATEGORIES } from "../../event-utils";
import cloneDeep from "lodash/cloneDeep";
import omit from "lodash/omit";
import pick from "lodash/pick";
import { convertObj } from "@/helpers/utils";
import { TIMEZONE_OPTION } from "@/constants/Timezone";
import {
  formatDateClient,
  getTimeString,
  addDayOfDate,
  subtractDayOfDate
} from "@/helpers/datetime-helper";
import { store } from "@/store";

const initEventCfg = () => ({
  name: "",
  category: "",
  timezone: "",
  isAllDay: false,
  startDate: null,
  endDate: null,
  startTime: "",
  endTime: "",
  location: "",
  description: ""
});

function convertEventCfgData(isisEvent) {
  const eventCfgData = convertObj.snakeCaseToCamelCase(isisEvent);
  const { start, end, timezone, isAllday } = eventCfgData;

  eventCfgData.startDate = getTimeString(start, timezone, formatDateClient);

  eventCfgData.endDate = isAllday
    ? subtractDayOfDate(end, 1)
    : getTimeString(end, timezone, formatDateClient);

  eventCfgData.startTime = isAllday
    ? ""
    : getTimeString(start, timezone, "HH:mm");
  eventCfgData.endTime = isAllday ? "" : getTimeString(end, timezone, "HH:mm");

  return pick(
    omit(eventCfgData, ["start", "end"]),
    Object.keys(initEventCfg())
  );
}

export default {
  name: "school-event-form",
  props: ["isisEvent"],
  data() {
    const timeZone = store.getters["user/timezone"];
    const eventCfg = this.isisEvent
      ? convertEventCfgData(this.isisEvent)
      : initEventCfg();

    return {
      categories: omit(EVENT_CATEGORIES, ["schedule", "meeting"]),
      timeZoneOpts: TIMEZONE_OPTION,
      tzDisplayValue: eventCfg.timezone || timeZone,
      eventCfg
    };
  },
  computed: {},
  methods: {
    getEventCfg() {
      const eventCfg = cloneDeep(this.eventCfg);
      if (eventCfg.isAllDay) {
        eventCfg.start = eventCfg.startDate;
        //add addition one day
        eventCfg.end = addDayOfDate(eventCfg.endDate, 1);
        delete eventCfg.timezone;
      } else {
        eventCfg.start = `${eventCfg.startDate} ${eventCfg.startTime}`;
        eventCfg.end = `${eventCfg.endDate} ${eventCfg.endTime}`;
      }

      const schoolSchedule = omit(eventCfg, [
        "startDate",
        "endDate",
        "startTime",
        "endTime"
      ]);
      return convertObj.camelCaseToSnakeCase(schoolSchedule);
    },
    resetEventCfg() {
      this.eventCfg = initEventCfg();
    }
  }
};
</script>

<style lang="scss" scoped>
.isis-switch-field {
  margin-top: 30px;
}
.flex-w-auto {
  flex: 1;
}
</style>
